import { Inject, Injectable, OnDestroy } from '@angular/core';
import { HttpErrorResponse, HttpClient } from '@angular/common/http';
import { BehaviorSubject, Subscription, throwError } from 'rxjs';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { APP_CONFIG } from '../../../../src/app/config/app-config.module';
import { AppConfig } from '../config-models/app-config.model';
import { saveUploadedFileToStore } from '../../../../src/app/features/employee-detail/actions/get-uploaded-doc.actions';
import { ApiErrorCodes, GenericErrorCodes } from '../../shared/enums';
import { Observable } from 'rxjs';
import * as getSelectedEmployeeSelector from '../../features/employee-search/selectors/selected-employee.selectors';
import * as logout from '../../features/auth/actions/logout.actions';
import { ILogoutRequest } from '../../../../src/app/features/auth/models/logout.model';
import notify from 'devextreme/ui/notify';

@Injectable()
export class HelpersService implements OnDestroy {
  screenName: string;
  subscription: Subscription = new Subscription();
  getEmployeeDetailId$: Observable<any>;

  invitesFirstTimeSearch: BehaviorSubject<boolean>;
  invitesFirstTime = true;

  legalEntitySetupFilterReset: BehaviorSubject<void>;
  memberOnBoardFilterReset: BehaviorSubject<void>;
  leReset: any;
  activeMessages: any[];

  // Flag to track if 401 is being handled - suppress all other errors when true
  private is401BeingHandled: boolean = false;

  constructor(
    private store: Store,
    private router: Router,
    @Inject(APP_CONFIG) private appConfig: AppConfig
  ) {
    this.getEmployeeDetailId$ = this.store.select(getSelectedEmployeeSelector.getSelectedEmployee);
    this.invitesFirstTimeSearch = new BehaviorSubject(this.invitesFirstTime);
    this.legalEntitySetupFilterReset = new BehaviorSubject(this.leReset);
    this.memberOnBoardFilterReset = new BehaviorSubject(this.leReset);
    this.activeMessages = [];
  }
  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  /**
   * Set flag to indicate 401 is being handled - this suppresses all other error notifications
   */
  set401HandlingFlag(value: boolean): void {
    this.is401BeingHandled = value;
  }

  handleError(error: HttpErrorResponse | Error | any) {
    if (this.is401BeingHandled) {
      return throwError(() => true);
    }

    // Check if 401 was already handled by UnauthorizedInterceptor
    // The interceptor throws a new Error('401_UNAUTHORIZED_HANDLED'), so check for that
    if (error instanceof Error) {
      if (error.message === '401_UNAUTHORIZED_HANDLED' || error.message === '401_ALREADY_HANDLED') {
        // Set flag to suppress all other errors
        this.is401BeingHandled = true;
        // Already handled by interceptor, just return without showing additional errors
        return throwError(() => true);
      }
    }

    // Check if it's an HttpErrorResponse with 401
    if (error && typeof error === 'object' && 'status' in error) {
      const is401Error = error.status === 401 ||
                        error?.error?.status === 401 ||
                        (error?.error?.error && error.error.error.StatusCode === 401);

      if (is401Error) {
        // Set flag immediately to suppress all other errors
        this.is401BeingHandled = true;
        // Check if error message indicates it was already handled by interceptor
        const errorMessage = error.message || (error as any)?.error?.message || '';
        if (errorMessage === '401_UNAUTHORIZED_HANDLED' ||
            errorMessage === '401_ALREADY_HANDLED') {
          // Already handled by interceptor, just return without showing additional errors
          return throwError(() => true);
        }
      }
    }

    // If error is already handled by interceptor (Error instance), just return
    if (error instanceof Error && (error.message === '401_UNAUTHORIZED_HANDLED' || error.message === '401_ALREADY_HANDLED')) {
      this.is401BeingHandled = true;
      return throwError(() => true);
    }

    // If error doesn't have status property, it's not an HttpErrorResponse
    if (!error || typeof error !== 'object' || !('status' in error)) {
      // Check flag again before returning
      if (this.is401BeingHandled) {
        return throwError(() => true);
      }
      return throwError(() => error);
    }

    if (this.is401BeingHandled) {
      return throwError(() => true);
    }

    var statusCounter = this.getCounter(error.status);
    if (error.status === 401 || error.status === 403) {
      // This should rarely be reached now (interceptor handles it first)
      // But keep as fallback for edge cases
      if (error.status === 401 && statusCounter > 0) {
        // skip this case
      } else {
        // Extract message from error?.error?.Message || error?.error?.message if available
        let errorMessage = 'Your session has expired, please log in again.';
        if (error?.error?.message) {
          errorMessage = error?.error?.Message || error?.error?.message;
        } else if (error?.error?.Message) {
          try {
            const messageStr = error?.error?.Message || error?.error?.message || '';
            if (typeof messageStr === 'string' && messageStr.startsWith('{') && messageStr.includes('"message"')) {
              const parsed = JSON.parse(messageStr);
              if (parsed?.message) {
                errorMessage = parsed.message;
              } else if (messageStr) {
                errorMessage = messageStr;
              }
            } else if (messageStr) {
              errorMessage = messageStr;
            }
          } catch {
            const messageStr = error?.error?.Message || error?.error?.message;
            if (messageStr) {
              errorMessage = messageStr;
            }
          }
        }

        this.notifyError(errorMessage, 300, 3000);
        const token = sessionStorage.getItem('token');
        const req: ILogoutRequest = {
          token,
          uniqueDeviceId: '00000000-0000-0000-0000-000000000000'
        };
        this.store.dispatch(logout.loadLogoutRequest({ request: req }));
        this.router.navigateByUrl('auth/sign-up');

        this.addMessageType({ status: error.status, counter: statusCounter + 1, datetime: new Date() });
      }
      return throwError(() => true);
    } else if (error.status === 503) {
      // Suppress 503 errors if 401 is being handled
      if (this.is401BeingHandled) {
        return throwError(() => true);
      }

      // server not responding
      if (statusCounter >= 3) {
        this.notifyError('Server is not responding now, lets please try after sometime.', 300, 3000);
        const token = sessionStorage.getItem('token');
        const req: ILogoutRequest = {
          token,
          uniqueDeviceId: '00000000-0000-0000-0000-000000000000'
        };
        this.store.dispatch(logout.loadLogoutRequest({ request: req }));
        this.router.navigateByUrl('auth/sign-up');
      } else {
        this.addMessageType({ status: error.status, counter: statusCounter + 1, datetime: new Date() });
      }
      return throwError(() => true);
    } else if (error.status === 400 || error.status === 402 || (error.status >= 403 && error.status <= 499)) {
      // Suppress 400-499 errors if 401 is being handled
      if (this.is401BeingHandled) {
        return throwError(() => true);
      }

      if (error.status === 404 && error.error && (error?.error?.Message || error?.error?.message) == 'MonolithApiException: ') {
        // Suppress 404 error with MonolithApiException
      } else {
        if(error.url && error.url.indexOf('GetBotflowPlayback') > -1){
        this.notifyError('File not found.', 250, 3000);
        }else{
        const errorMsg = error?.error?.Message || error?.error?.message || 'Bad Request.';
        this.notifyError(errorMsg, (error?.error?.message || error?.error?.Message) ? 500 : 250, 3000);
        }
      }
      return throwError(() => true);
    } else if (error.status === 500) {
      // Suppress 500 errors if 401 is being handled
      if (this.is401BeingHandled) {
        return throwError(() => true);
      }
      this.notifyError(`Server Error! ` + ApiErrorCodes.SERVER_ERROR, 600, 3000);
      return throwError(() => true);
    }
    return throwError(error);
  }

  getCounter(status) {
    var activeMsgs = localStorage.getItem('MULTIPLE_ERROR_HANDLING');
    if (activeMsgs) {
      var messages = JSON.parse(activeMsgs);
      var filteredMessages = messages.filter(m => m.status == status);
      return filteredMessages.length;
    } else return 0;
  }

  getLastErrorTime(status) {
    var activeMsgs = localStorage.getItem('MULTIPLE_ERROR_HANDLING');
    if (activeMsgs) {
      var messages = JSON.parse(activeMsgs);
      var filteredMessages = messages.filter(m => m.status == status).sort(m => m.counter);
      return filteredMessages.length;
    } else return 0;
  }

  addMessageType(message) {
    this.activeMessages.push(message);
    localStorage.setItem('MULTIPLE_ERROR_HANDLING', JSON.stringify(this.activeMessages));
  }

  /**
   * Check if error is a 401 that was already handled by UnauthorizedInterceptor
   */
  private is401AlreadyHandled(error: any): boolean {
    if (!error) return false;

    const is401Error = error.status === 401 ||
                      error?.error?.status === 401 ||
                      (error?.error?.error && error.error.error.StatusCode === 401);

    if (!is401Error) return false;

    // Check if error message indicates it was already handled
    const errorMessage = error.message || error?.error?.message || '';
    return errorMessage === '401_UNAUTHORIZED_HANDLED' ||
           errorMessage === '401_ALREADY_HANDLED';
  }

  handleGeneralErrors(displayErrorSupplied, summary, message, error?: any) {
    // Skip error handling if 401 is being handled - suppress all other errors
    if (this.is401BeingHandled) {
      return;
    }

    // Also check if this specific error is a 401 that was already handled
    if (error && this.is401AlreadyHandled(error)) {
      return;
    }

    if (message && displayErrorSupplied) {
      this.notifyError(message, 400, 3000);
    } else {
      this.notifyError(GenericErrorCodes.DEFAULT_ERROR, 400, 3000);
    }
  }

  private checkSessionStorage(token: string) {
    const isSessionToken = !!sessionStorage.getItem('token'); // check if the token exists using its name
    const sessionTokenString = sessionStorage.getItem('token'); // get the token string

    if (token.length > 0) {
      return token;
    } else if (isSessionToken && sessionTokenString && sessionTokenString.length > 0) {
      return sessionTokenString;
    } else {
      return throwError('no token found in Session Storage');
    }
  }

  defaultHeaders(token: string) {
    const businessId = sessionStorage.getItem('businessId');
    const headers: { [key: string]: string } = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${this.checkSessionStorage(token)}`
    };
    if (businessId && businessId.trim() !== '') {
      headers['X-Legal-Entity-Id'] = businessId;
    }
    return { headers };
  }

  defaultImageUploadHeaders(token: string) {
    const businessId = sessionStorage.getItem('businessId');
    const headers: { [key: string]: string } = {
      Accept: 'multipart/form-data',
      'Content-Type': 'application/x-www-form-urlencoded',
      Authorization: `Bearer ${this.checkSessionStorage(token)}`
    };
    if (businessId && businessId.trim() !== '') {
      headers['X-Legal-Entity-Id'] = businessId;
    }
    return { headers };
  }

  defaultPDFHeaders(token: string) {
    const businessId = sessionStorage.getItem('businessId');
    const headers: { [key: string]: string } = {
      Accept: 'application/json',
      'Content-Type': 'application/pdf',
      Authorization: `Bearer ${this.checkSessionStorage(token)}`
    };
    if (businessId && businessId.trim() !== '') {
      headers['X-Legal-Entity-Id'] = businessId;
    }
    return { headers };
  }

  allImageHeaders(token: string) {
    const businessId = sessionStorage.getItem('businessId');
    const headers: { [key: string]: string } = {
      Accept: 'application/octet-stream',
      'Content-Type': 'application/octet-stream',
      Authorization: `Bearer ${this.checkSessionStorage(token)}`
    };
    if (businessId && businessId.trim() !== '') {
      headers['X-Legal-Entity-Id'] = businessId;
    }
    return {
      headers,
      responseType: 'blob' as 'json'
    };
  }

  downloadFile(data: any) {
    let newBase64StringUploaded: any;
    let newBase64Str: string;
    const blob = new Blob([data], { type: 'application/octet-stream' });
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    newBase64StringUploaded = '';
    newBase64Str = '';
    reader.onload = () => {
      newBase64StringUploaded = reader.result;
      newBase64Str = newBase64StringUploaded.replace(/^[^,]+, */, '');
      this.store.dispatch(saveUploadedFileToStore({ savedBase64: newBase64Str }));
    };
  }

  checkBusinessIdState(businessId: string): string {
    let bizId: string;
    const seshBizId = sessionStorage.getItem('businessId');
    bizId = businessId === undefined ? seshBizId : businessId;
    return bizId;
  }

  checkCustomerIdState(): string {
    let userProfileId;
    const cust = JSON.parse(sessionStorage.getItem('customerId'));
    userProfileId = userProfileId === undefined ? cust.userProfileId : userProfileId;
    return userProfileId;
  }

  getSelectedLegalEntity(entity: string): string {
    let le = entity === undefined ? JSON.parse(sessionStorage.getItem('legalEntity')) : entity;
    return le;
  }

  decodeJwtToken(token: string) {
    let userRoles: string[];
    var payLoad = JSON.parse(atob(token.split('.')[1]));
    if (Array.isArray(payLoad.role)) {
      userRoles = payLoad.role;
    }
  }

  errorDecoder(error: string): string {
    let parsedError: string;
    switch (true) {
      case error.includes('This mobile number is already registered'):
        parsedError = ApiErrorCodes.MOBILE_ALREADY_REGISTERED;
        break;
      case error.includes('This email address is already registered'):
        parsedError = ApiErrorCodes.EMAIL_ALREADY_REGISTERED;
        break;
      case error.includes('Internal Server Error'):
        parsedError = ApiErrorCodes.SERVER_ERROR;
        break;
      case error.includes(
        "Internal Server Error : Internal Server Error : Validation failed for one or more entities. See 'EntityValidationErrors' property for more details."
      ):
        parsedError = ApiErrorCodes.SERVER_ERROR;
        break;
      case error.includes('This business name already exists'):
        parsedError = ApiErrorCodes.BUSINESS_EXISTS;
        break;
      case error.includes('This business already has an EFT payment method'):
        parsedError = ApiErrorCodes.EFT_EXISTS;
        break;
      default:
        parsedError = ApiErrorCodes.GENERIC_ERROR;
        break;
    }
    return parsedError;
  }

  permissionValidation(role: string): boolean {
    switch (role) {
      case 'SuperUser':
        return true;
      case 'BusinessSuperUser':
        return true;
      case 'BusinessAdmin':
        return true;
      case 'FICAVerifyUser':
        return true;
      case 'FICAApprovalUser':
        return true;
      default:
        return false;
    }
  }

  inviteFirstTimeSearchControl(firstTime: boolean) {
    this.invitesFirstTimeSearch.next(firstTime);
  }

  parseBlobErrorResponse(error: Error | any) {
    if (error instanceof HttpErrorResponse && error.error instanceof Blob && error.error.type === 'application/json') {
      // https://github.com/angular/angular/issues/19888
      // When request of type Blob, the error is also in Blob instead of object of the json data
      return new Promise<string>((resolve, reject) => {
        let reader = new FileReader();
        reader.onload = (e: Event) => {
          try {
            const errmsg = JSON.parse((<any>e.target).result);
            const message =
              errmsg.Message === '{"message":"There is no products linked to your business."}'
                ? 'There are no products linked to your business.'
                : error?.error?.Message || error?.error?.message;
            this.notifyError(message, 400, 3000);
          } catch (e) {
            reject(error);
          }
        };
        reader.onerror = e => {
          reject(error);
        };
        reader.readAsText(error.error);
      });
    }
  }

  resetFilterStateLegalEntitySetup() {
    this.legalEntitySetupFilterReset.next();
  }

  resetFilterStateMemberOnBoard() {
    this.memberOnBoardFilterReset.next();
  }

  notifyError(message, width, delay){
    if (this.is401BeingHandled) {
      // Only allow 401-related messages to be shown
      const is401Message = message.includes('session has expired') ||
                          message.includes('Unauthorized') ||
                          message.includes('401');
      if (!is401Message) {
        return;
      }
    }

    notify(
      {
          message: `Error! ` + message,
          width: width,
          shading: true
      },
      'error',
      (delay?delay:3000)
    );
  }

  notifySuccess(message, width, delay){
    notify(
      {
          message: `Success! ` + message,
          width: width,
          shading: true
      },
      'success',
      (delay?delay:3000)
    );
  }

  notifyInfo(message, width, delay){
    notify(
      {
          message: message,
          width: width,
          shading: true
      },
      'info',
      (delay?delay:3000)
    );
  }

  notifyWarning(message, width, delay){
    notify(
      {
          message: message,
          width: width,
          shading: true
      },
      'warning',
      (delay?delay:3000)
    );
  }

  /**
   * Parses nested error messages from API responses
   * Handles error format: {"Message":"{\"message\":\"Actual error message\"}","StatusCode":400,"Stacktrace":null}
   * @param error The error object from HTTP response
   * @returns Parsed error message or fallback message
   */
  parseApiErrorMessage(error: any): string {
    try {
      // Check if error has the nested Message format (uppercase)
      if (error?.error?.Message) {
        const messageStr = error?.error?.Message || error?.error?.message || '';

        // Check if Message is a JSON string that needs parsing
        if (typeof messageStr === 'string' && messageStr.startsWith('{') && messageStr.includes('"message"')) {
          try {
            const parsedMessage = JSON.parse(messageStr);
            if (parsedMessage?.message) {
              return parsedMessage.message;
            }
          } catch (parseError) {
            // If parsing fails, return the raw Message
            return messageStr;
          }
        }

        // If Message is already a plain string, return it
        return messageStr;
      }

      // Check for lowercase message property
      if (error?.error?.message) {
        return error?.error?.Message || error?.error?.message;
      }

      // Fallback to standard error message extraction
      return error?.message || 'An error occurred';
    } catch (e) {
      // If anything fails, return a generic error message
      return 'An error occurred while processing the response';
    }
  }
}
